import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, CreditCard } from "lucide-react";
import { startCheckout } from "@/utils/checkout";

export default function StripeSignup() {
  const { signupStripe, user, loading } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // If they're already signed in, hand them off to checkout or dashboard.
  useEffect(() => {
    if (!loading && user) {
      const pending = sessionStorage.getItem("vbs_pending_tier");
      if (pending && searchParams.get("next") === "checkout") {
        sessionStorage.removeItem("vbs_pending_tier");
        startCheckout(pending).catch(() => nav("/dashboard"));
      } else {
        nav("/dashboard", { replace: true });
      }
    }
  }, [loading, user, nav, searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    const res = await signupStripe(email, password, fullName);
    if (!res.ok) { setBusy(false); return toast.error(res.error); }
    toast.success("Account created — taking you to checkout");
    const pending = sessionStorage.getItem("vbs_pending_tier");
    if (pending && searchParams.get("next") === "checkout") {
      sessionStorage.removeItem("vbs_pending_tier");
      try { await startCheckout(pending); return; }
      catch { toast.error("Could not start checkout"); }
    }
    setBusy(false);
    nav("/dashboard");
  };

  return (
    <div className="vbs-auth-bg" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div className="vbs-card" style={{ width: "100%", maxWidth: 460, padding: 36 }}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span className="vbs-logo-dot" />
            <span style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: "var(--white)" }}>
              Viral Book Suite
            </span>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: 2, color: "var(--gold-dim)", textTransform: "uppercase" }}>
            Create your account · Pay with Stripe
          </div>
        </div>
        <div style={{ background: "rgba(196,149,42,0.06)", border: "1px solid rgba(196,149,42,0.25)", borderRadius: 10, padding: "10px 12px", marginBottom: 18, display: "flex", gap: 8, alignItems: "center" }}>
          <CreditCard size={15} style={{ color: "var(--gold)" }} />
          <span style={{ fontSize: 12, color: "var(--gold-pale)" }}>You'll be taken to a secure Stripe checkout right after signup.</span>
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>Name</div>
            <input data-testid="stripe-signup-name" className="vbs-input" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </label>
          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>Email</div>
            <input data-testid="stripe-signup-email" className="vbs-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>Password</div>
            <input data-testid="stripe-signup-password" className="vbs-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
          </label>
          <button data-testid="stripe-signup-submit" type="submit" disabled={busy} className="vbs-btn vbs-btn-gold" style={{ padding: 14, marginTop: 6 }}>
            {busy ? "Creating…" : "Create account & continue to checkout"}
            <Sparkles size={15} />
          </button>
        </form>

        <div style={{ marginTop: 22, textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
          Already have an account? <Link to="/stripe-signin" data-testid="stripe-signin-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
